import { fetchAuthSession, fetchUserAttributes, signInWithRedirect, signOut as amplifySignOut } from 'aws-amplify/auth';
import type { FetchUserAttributesOutput } from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import { createContext, PropsWithChildren, useCallback, useContext, useEffect, useState } from 'react';

import { FEDERATE_PROVIDER_NAME } from 'src/auth/cognito-config';

/**
 * Authentication states.
 * - `configuring`   : resolving the session or navigating to Midway (app shows a loader).
 * - `authenticated` : a valid Cognito session is present; the app can render.
 * - `error`         : the redirect to Midway could not be initiated.
 *
 * There is no `unauthenticated` state: this is an internal Midway site, so when
 * there is no session the provider redirects to the hosted UI automatically —
 * there is no login screen.
 */
export type AuthStatus = 'configuring' | 'authenticated' | 'error';

/** All Cognito user-pool attributes (standard + custom) returned by fetchUserAttributes. */
export type UserAttributes = FetchUserAttributesOutput;

export interface AuthContextValue {
  readonly status: AuthStatus;
  /** All user attributes from Cognito (fetchUserAttributes), e.g. email, given_name, custom:employeeNumber. */
  readonly attributes?: UserAttributes;
  readonly signOut: () => Promise<void>;
  /** Current id token JWT (refreshed near expiry). Use as the API Authorization header. */
  readonly getIdToken: () => Promise<string | undefined>;
  /** Current access token JWT (refreshed near expiry). */
  readonly getAccessToken: () => Promise<string | undefined>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

/**
 * In-flight guard so a burst of re-renders / StrictMode's double-invoke can't
 * fire `signInWithRedirect` twice. Module-scoped to be shared across renders.
 */
let redirecting = false;

const redirectToMidway = async (): Promise<void> => {
  if (redirecting) {
    return;
  }
  redirecting = true;
  // Straight to Federate (the only IdP), so no provider-chooser screen.
  await signInWithRedirect({ provider: { custom: FEDERATE_PROVIDER_NAME } });
};

/**
 * Provides auth state + token access via Amplify. Amplify must be configured
 * (Amplify.configure) before this mounts. On mount it reads the session; if
 * there is none it redirects to the Cognito hosted UI (Federate -> Midway). It
 * subscribes to Amplify `Hub` auth events so the OAuth redirect return and token
 * refresh re-resolve the session.
 */
export const AuthProvider = ({ children }: PropsWithChildren) => {
  const [status, setStatus] = useState<AuthStatus>('configuring');
  const [attributes, setAttributes] = useState<UserAttributes | undefined>(undefined);

  const load = useCallback(async () => {
    try {
      const session = await fetchAuthSession();
      if (session.tokens?.idToken) {
        // Custom attributes are not in the hosted-UI id token; read them via
        // the GetUser API (needs the aws.cognito.signin.user.admin scope).
        setAttributes(await fetchUserAttributes());
        setStatus('authenticated');
        return;
      }
    } catch {
      // No session — fall through to the redirect.
    }
    try {
      await redirectToMidway();
    } catch {
      setStatus('error');
    }
  }, []);

  useEffect(() => {
    // Subscribe before the first read so the OAuth-return event is never missed.
    const unsubscribe = Hub.listen('auth', ({ payload }) => {
      if (payload.event === 'signedIn' || payload.event === 'signInWithRedirect' || payload.event === 'tokenRefresh') {
        void load();
      }
    });
    void load();
    return unsubscribe;
  }, [load]);

  const signOut = useCallback(async () => {
    setStatus('configuring');
    setAttributes(undefined);
    await amplifySignOut();
  }, []);

  const getIdToken = useCallback(async () => (await fetchAuthSession()).tokens?.idToken?.toString(), []);
  const getAccessToken = useCallback(async () => (await fetchAuthSession()).tokens?.accessToken?.toString(), []);

  const value: AuthContextValue = { status, attributes, signOut, getIdToken, getAccessToken };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

/** Access the auth context. Must be used within an {@link AuthProvider}. */
export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
